import React, { useState, useRef, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStatus, useToken } from '../../utils'
import {
	usePreContractList,
	useCancelPreContract,
	useAcceptPreContract,
	useRejectPreContract,
} from '../../hooks/offer'
import styles from './PreContractManagementDefaultView.module.scss'
import { formatDateTimeToYYYYMMDDHHMM } from '../../utils'
import { useUser } from '../../hooks'
import { useCaddieProfileModal, useProProfileModal } from '../../contexts/ModalContext'
import { AcceptedPreContractsSection } from './AcceptedPreContractsSection'

const TAB_MENU = [
	{ value: 'received', label: '받은 제안' },
	{ value: 'sent', label: '보낸 제안' },
	{ value: 'past', label: '지난 내역' },
]

export const PreContractManagementDefaultView = ({ ...props }) => {
	const [auth, onChangeAuthStatus] = useAuthStatus()

	const { data: userInfo } = useUser()
	const token = useToken()
	const location = useLocation()

	const EMPTY_STATE_CONFIG = useMemo(() => {
		return {
			received: {
				title: '받은 제안이 아직 없어요.',
				searchLink: '/search',
			},
			sent: {
				title: '보낸 제안이 아직 없어요.',
				searchLink: '/search',
			},
			past: {
				title: '지난 내역이 아직 없어요.',
				searchLink: '/search',
			},
		}
	}, [userInfo?.user_type])

	// react-query에서 상태 관리하므로 로컬 상태 제거
	// location state에서 기본 탭 확인, 없으면 세션 스토리지에서 복원, 없으면 기본값 'received'
	const [selectedTab, setSelectedTab] = useState(() => {
		// 사전계약 생성 후 이동한 경우 'sent' 탭으로 설정
		if (location.state?.defaultTab) {
			return location.state.defaultTab
		}
		// 그 외의 경우 세션 스토리지에서 복원
		return sessionStorage.getItem('precontract-selected-tab') || 'received'
	})
	const [sortOrder, setSortOrder] = useState(() => {
		return sessionStorage.getItem('precontract-sort-order') || 'newest'
	})

	// 사전계약 취소 mutation
	const cancelPreContractMutation = useCancelPreContract()

	// 사전계약 수락 mutation
	const acceptPreContractMutation = useAcceptPreContract()

	// 사전계약 거절 mutation
	const rejectPreContractMutation = useRejectPreContract()

	// 프로필 모달
	const caddieProfileModal = useCaddieProfileModal()
	const proProfileModal = useProProfileModal()

	const navigate = useNavigate()

	// 사전계약 목록 조회
	const { data: preContractData, isLoading, error, refetch } = usePreContractList(token)

	// 로딩 상태
	if (isLoading) {
		return (
			<div className={styles.loadingContainer}>
				<div className={styles.loadingSpinner}></div>
				<div className={styles.loadingText}>사전계약 목록을 불러오는 중...</div>
			</div>
		)
	}

	// 에러 상태
	if (error) {
		return (
			<div className={styles.errorContainer}>
				<div className={styles.errorText}>
					사전계약 목록을 불러오는 중 오류가 발생했습니다.
				</div>
				<button className={styles.retryButton} onClick={() => refetch()}>
					다시 시도
				</button>
			</div>
		)
	}

	// 탭 변경 핸들러
	const handleTabChange = (tabValue) => {
		setSelectedTab(tabValue)
	}

	// 정렬 변경 핸들러
	const handleSortChange = () => {
		const newSortOrder = sortOrder === 'newest' ? 'oldest' : 'newest'
		setSortOrder(newSortOrder)
		sessionStorage.setItem('precontract-sort-order', newSortOrder)
	}

	// 사전계약 취소 핸들러
	const handleCancelPreContract = (preContractId) => {
		if (cancelPreContractMutation.isPending) return

		const confirmCancel = window.confirm('사전계약을 취소하시겠습니까?')
		if (!confirmCancel) return

		cancelPreContractMutation.mutate(
			{ token, preContractId },
			{
				onSuccess: () => {
					alert('사전계약이 취소되었습니다.')
				},
				onError: () => {
					alert('사전계약 취소에 실패했습니다.')
				},
			},
		)
	}

	// 사전계약 수락 핸들러
	const handleAcceptPreContract = (preContractId) => {
		if (acceptPreContractMutation.isPending) return

		const confirmAccept = window.confirm('사전계약을 수락하시겠습니까?')
		if (!confirmAccept) return

		acceptPreContractMutation.mutate(
			{ token, preContractId },
			{
				onSuccess: () => {
					alert('사전계약이 수락되었습니다.')
				},
				onError: () => {
					alert('사전계약 수락에 실패했습니다.')
				},
			},
		)
	}

	// 프로필 모달 열기 핸들러 (프로/캐디 구분)
	const handleShowProfile = (user) => {
		if (user?.user_type === 'tourPro') {
			proProfileModal.show({
				user: user,
				onClose: () => {
					console.log('프로 프로필 모달 닫힘')
				},
			})
		} else {
			caddieProfileModal.show({
				user: user,
				onClose: () => {
					console.log('캐디 프로필 모달 닫힘')
				},
			})
		}
	}

	// 캐디 프로필 모달 열기 핸들러 (하위 호환성)
	const handleShowCaddieProfile = (user) => {
		handleShowProfile(user)
	}

	// 체결된 사전계약 데이터 가져오기 (탭/정렬용 목록에서 사용)
	const getAcceptedPreContracts = () => {
		if (!preContractData || !preContractData.data || !auth?.user?.id) return []

		const items = preContractData.data || []
		return items.filter((item) => {
			if (!item) return false
			return item.pre_contract_status === 'ACCEPTED'
		})
	}

	// 현재 표시할 데이터 가져오기
	const getCurrentData = () => {
		if (!preContractData || !preContractData.data || !auth?.user?.id) return []

		const filtered = []
		const items = preContractData.data || []

		for (const item of items) {
			if (!item) continue

			// 사전계약 상태에 따른 필터링
			const isPending = item.pre_contract_status === 'PENDING'
			const isCompleted = item.pre_contract_status === 'ACCEPTED'
			const isRejectedOrCancelled =
				item.pre_contract_status === 'REJECTED' || item.pre_contract_status === 'CANCELLED'

			if (selectedTab === 'received') {
				// 받은 제안: target_user_id가 현재 사용자이고 Pending 상태
				const isReceived = item.target_user_id === auth.user.id
				if (isReceived && isPending) {
					filtered.push(item)
				}
			} else if (selectedTab === 'sent') {
				// 보낸 제안: user_id가 현재 사용자이고 Pending 상태
				const isSent = item.user_id === auth.user.id
				if (isSent && isPending) {
					filtered.push(item)
				}
			} else if (selectedTab === 'past') {
				// 지난 내역: 완료되거나 거절되거나 취소된 것들
				if (isCompleted || isRejectedOrCancelled) {
					filtered.push(item)
				}
			}
		}

		// 정렬 적용
		filtered.sort((a, b) => {
			const dateA = new Date(a.created_at)
			const dateB = new Date(b.created_at)

			if (sortOrder === 'newest') {
				return dateB - dateA // 최신순 (내림차순)
			} else {
				return dateA - dateB // 오래된순 (오름차순)
			}
		})

		return filtered
	}

	// 현재 데이터 가져오기
	const currentData = getCurrentData()

	// 빈 상태 렌더링
	const renderEmptyState = () => {
		const config = EMPTY_STATE_CONFIG[selectedTab]
		return (
			<div className={styles.emptyState}>
				<div>{config.title}</div>
				{config.searchLink && (
					<div onClick={() => navigate(config.searchLink)}>
						{userInfo?.user_type === 'tourPro' ? '캐디 찾기' : '프로 찾기'}
					</div>
				)}
			</div>
		)
	}

	// 사전계약 아이템 렌더링
	const renderPreContractItem = (item, index) => {
		// 날짜 포맷팅
		const formatDate = (dateString) => {
			const date = new Date(dateString)
			return date.toLocaleDateString('ko-KR', {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
			})
		}

		// 상태 한글 변환
		const getStatusText = (status) => {
			switch (status) {
				case 'PENDING':
					return '제안됨'
				case 'ACCEPTED':
					return '승인됨'
				case 'REJECTED':
					return '거절됨'
				case 'CANCELLED':
					return '취소됨'
				default:
					return '대기중'
			}
		}

		// 탭에 따른 문구 생성
		const getStatusMessage = (selectedTab, item, displayUser) => {
			const userTypeText = getUserTypeText(displayUser?.user_type)

			if (selectedTab === 'received') {
				return ` ${userTypeText}님에게 사전계약 제안이 도착했습니다.`
			}

			if (selectedTab === 'sent') {
				return ` ${userTypeText}님에게 사전계약 제안을 보냈습니다.`
			}

			if (selectedTab === 'past') {
				// target_user_id와 현재 사용자 ID를 비교해서 보낸 건지 받은 건지 구분
				const isReceivedByMe = item.target_user_id === auth?.user?.id

				if (isReceivedByMe) {
					return ` ${userTypeText}님에게 사전계약 제안이 도착했습니다.`
				} else {
					return ` ${userTypeText}님에게 사전계약 제안을 보냈습니다.`
				}
			}

			return ''
		}

		// 사용자 타입 텍스트 가져오기
		const getUserTypeText = (userType) => {
			if (userType === 'tourCaddie' || userType === 'houseCaddie') {
				return '캐디'
			}
			if (userType === 'tourPro') {
				return '프로'
			}
			return userType
		}

		// 표시할 사용자 결정
		const getDisplayUser = () => {
			if (selectedTab === 'received') {
				// 받은 제안: user_id가 제안한 사람
				return item.user
			} else if (selectedTab === 'sent') {
				// 보낸 제안: target_user_id가 받은 사람
				return item.targetUser
			} else if (selectedTab === 'past') {
				// 지난 내역: target_user_id에 따라 결정
				return item.target_user_id === auth.user.id ? item.user : item.targetUser
			}
			return null
		}

		const displayUser = getDisplayUser()

		// 상태 배지 렌더링
		const renderStatusBadge = () => {
			const status = item.pre_contract_status
			if (status === 'PENDING') {
				return <div className={styles.status}>제안됨</div>
			}
			if (status === 'ACCEPTED') {
				return <div className={styles.status}>승인됨</div>
			}
			if (status === 'REJECTED') {
				return <div className={styles.status}>거절됨</div>
			}
			if (status === 'CANCELLED') {
				return <div className={styles.status}>취소됨</div>
			}
			return <div className={styles.status}>대기중</div>
		}

		return (
			<div key={index} className={styles.contractItem}>
				{/* Header */}
				<div className={styles.contractItemHeader}>
					<div className={styles.contractItemHeaderDetail}>
						<div>{formatDateTimeToYYYYMMDDHHMM(item.created_at)}</div>
						{/* <div className={styles.suggestType}>
							<div style={{ color: 'white' }}>사전계약</div>
							<svg
								width="11"
								height="10"
								viewBox="0 0 11 10"
								fill="none"
								xmlns="http://www.w3.org/2000/svg">
								<path
									fillRule="evenodd"
									clipRule="evenodd"
									d="M3.7155 0.899378C4.12759 0.425466 4.73572 0.125 5.41406 0.125C6.09237 0.125 6.70048 0.425436 7.11256 0.899307C7.73911 0.855532 8.38167 1.07308 8.86138 1.55279C9.34108 2.03249 9.55863 2.67504 9.51486 3.3016C9.98867 3.71368 10.2891 4.32174 10.2891 5C10.2891 5.67838 9.98856 6.28655 9.51459 6.69864C9.55827 7.32509 9.34072 7.96751 8.86109 8.44713C8.38147 8.92675 7.73906 9.14431 7.11261 9.10063C6.70052 9.57454 6.0924 9.875 5.41406 9.875C4.73576 9.875 4.12766 9.57457 3.71558 9.10071C3.08902 9.14449 2.44646 8.92694 1.96675 8.44723C1.48704 7.96752 1.26949 7.32496 1.31327 6.69841C0.839455 6.28632 0.539062 5.67826 0.539062 5C0.539062 4.32171 0.839486 3.71361 1.31334 3.30153C1.26961 2.67502 1.48717 2.03254 1.96683 1.55287C2.4465 1.0732 3.089 0.855647 3.7155 0.899378ZM7.21921 4.09296C7.33959 3.92443 7.30056 3.69023 7.13203 3.56985C6.9635 3.44947 6.72929 3.48851 6.60891 3.65704L4.99121 5.92182L4.17923 5.10984C4.03278 4.96339 3.79534 4.96339 3.6489 5.10984C3.50245 5.25628 3.50245 5.49372 3.6489 5.64016L4.7739 6.76517C4.85184 6.84311 4.96014 6.88281 5.06999 6.87372C5.17985 6.86463 5.28014 6.80766 5.34421 6.71796L7.21921 4.09296Z"
									fill="white"
								/>
							</svg>
						</div> */}
					</div>
					<div className={styles.contractItemHeaderBack}>
						<div>{renderStatusBadge()}</div>
					</div>
				</div>

				{/* Body */}
				<div className={styles.contractItemBody}>
					<div>
						{displayUser?.profile_type && displayUser.profile_type !== 'unknown' ? (
							<img
								src={displayUser.profile_type}
								alt={`${displayUser.name} 프로필`}
								style={{
									width: '48px',
									height: '48px',
									borderRadius: '50%',
									objectFit: 'cover',
								}}
							/>
						) : (
							<img
								src={
									displayUser?.user_type === 'tourPro'
										? '/images/pro-default.png'
										: '/images/caddie-default-profile.svg'
								}
								alt="기본 프로필"
								style={{
									width: '48px',
									height: '48px',
									borderRadius: '50%',
									objectFit: 'cover',
								}}
							/>
						)}
					</div>
					<div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
						<div
							className={styles.profileInfo}
							onClick={() => {
								handleShowCaddieProfile(displayUser)
							}}>
							{/* <div
								style={{
									fontSize: '12px',
									fontWeight: '500',
									color: 'white',
									backgroundColor: 'black',
									padding: '0px 8px',
									borderRadius: '4px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}>
								{displayUser?.user_type === 'tourCaddie'
									? '투어 캐디'
									: displayUser?.user_type === 'houseCaddie'
										? '하우스 캐디'
										: displayUser?.user_type === 'tourPro'
											? '투어 프로'
											: '사용자'}
							</div> */}
							<span className={styles.userName}>{displayUser?.name}</span>
							<span className={styles.statusMessage}>
								{getStatusMessage(selectedTab, item, displayUser)}
							</span>
						</div>
						<div className={styles.tourInformation}>
							{item?.message && (
								<div className={styles.userMessage}>"{item.message}"</div>
							)}
						</div>
					</div>
				</div>

				{/* Footer */}
				<div className={styles.contractItemFooter}>
					{selectedTab === 'received' && (
						<>
							<button
								className={styles.acceptButton}
								onClick={() => handleAcceptPreContract(item.id)}
								disabled={acceptPreContractMutation.isPending}>
								{acceptPreContractMutation.isPending
									? '처리중...'
									: '제안 수락하기'}
							</button>
							<button
								className={styles.rejectButton}
								onClick={() => {
									rejectPreContractMutation.mutate({
										token: auth?.token,
										preContractId: item.id,
									})
								}}
								disabled={rejectPreContractMutation.isPending}>
								{rejectPreContractMutation.isPending ? '처리중...' : '거절하기'}
							</button>
						</>
					)}
					{selectedTab === 'sent' && (
						<button
							className={styles.cancelButton}
							onClick={() => handleCancelPreContract(item.id)}
							disabled={cancelPreContractMutation.isPending}>
							{cancelPreContractMutation.isPending ? '처리중...' : '제안 취소하기'}
						</button>
					)}
				</div>
			</div>
		)
	}

	return (
		<div className={styles.preContractManagementContainer}>
			<div className={styles.title}>
				<div>사전계약 관리</div>
				<div>2026년 시즌을 위한 사전계약을 관리해보세요!</div>
			</div>

			<AcceptedPreContractsSection variant="management" showTitle />

			<div className={styles.tabContainer}>
				<div className={styles.userSelectType}>
					{TAB_MENU.map((tab) => (
						<div
							key={tab.value}
							className={selectedTab === tab.value ? styles.active : ''}
							onClick={() => handleTabChange(tab.value)}>
							{tab.label}
						</div>
					))}
				</div>
			</div>

			{/* 체결된 사전계약 섹션 */}

			<div className={styles.sortContainer}>
				<button className={styles.sortButton} onClick={handleSortChange}>
					<svg
						width="12"
						height="11"
						viewBox="0 0 12 11"
						fill="none"
						xmlns="http://www.w3.org/2000/svg">
						<path
							fillRule="evenodd"
							clipRule="evenodd"
							d="M0.3125 0.625C0.3125 0.383375 0.508375 0.1875 0.75 0.1875H9.0625C9.30412 0.1875 9.5 0.383375 9.5 0.625C9.5 0.866625 9.30412 1.0625 9.0625 1.0625H0.75C0.508375 1.0625 0.3125 0.866625 0.3125 0.625ZM8.75314 2.94064C8.924 2.76979 9.201 2.76979 9.37186 2.94064L11.5594 5.12814C11.7302 5.299 11.7302 5.576 11.5594 5.74686C11.3885 5.91771 11.1115 5.91771 10.9406 5.74686L9.5 4.30622L9.5 10.25C9.5 10.4916 9.30412 10.6875 9.0625 10.6875C8.82088 10.6875 8.625 10.4916 8.625 10.25L8.625 4.30622L7.18436 5.74686C7.0135 5.91771 6.7365 5.91771 6.56564 5.74686C6.39479 5.576 6.39479 5.29899 6.56564 5.12814L8.75314 2.94064ZM0.3125 3.25C0.3125 3.00838 0.508375 2.8125 0.75 2.8125H6.4375C6.67912 2.8125 6.875 3.00838 6.875 3.25C6.875 3.49162 6.67912 3.6875 6.4375 3.6875H0.75C0.508375 3.6875 0.3125 3.49162 0.3125 3.25ZM0.3125 5.875C0.3125 5.63338 0.508375 5.4375 0.75 5.4375H3.8125C4.05412 5.4375 4.25 5.63338 4.25 5.875C4.25 6.11662 4.05412 6.3125 3.8125 6.3125H0.75C0.508375 6.3125 0.3125 6.11662 0.3125 5.875Z"
							fill="#A0A0A0"
						/>
					</svg>

					{sortOrder === 'newest' ? '최신순' : '오래된순'}
				</button>
			</div>

			<div className={styles.contractList}>
				{currentData.length === 0 ? (
					renderEmptyState()
				) : (
					<div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
						{currentData.map((item, index) => renderPreContractItem(item, index))}
					</div>
				)}
			</div>
		</div>
	)
}
